/*
 * Copyright (c) 2025. Bolmate B.V.
 */
import './style/feedProductSelect.scss';

import {ChangeFunction, escapePressed, hasHover, remToPx} from '@dvrd/dvr-controls';

import {SelectableReviewProduct} from '../../../../util/types';

import React, {MouseEventHandler, useEffect, useMemo, useRef} from 'react';

import {useTranslation} from 'react-i18next';

import {BolmateButton, BolmateInput} from '../../../controls';

import classNames from 'classnames';

import InfiniteList, {ListLoader, RenderRowProps} from '../../../misc/infiniteList';

import {DotsLoader} from '../../../controls/loader/dotsLoader';

import Image from '../../../misc/image';

interface Props {
    onSearch: VoidFunction;
    onChangeSearch: ChangeFunction<string>;
    onClickProduct: (product: SelectableReviewProduct) => MouseEventHandler;
    onLoadMore: ListLoader;
    onOpen: MouseEventHandler;
    onClose: VoidFunction;
    loadingMore: boolean;
    search: string;
    loading: boolean;
    products: Array<SelectableReviewProduct>;
    total: number;
    open: boolean;
}

export default function FeedProductSelect(props: Props) {
    const {t} = useTranslation();
    const {
        onSearch,
        onChangeSearch,
        onClickProduct,
        onLoadMore,
        search,
        loading,
        products,
        total,
        loadingMore,
        open,
        onClose,
        onOpen
    } = props;
    const hasMore = useMemo(() => {
        return products.length < total;
    }, [products, total]);
    const eventController = useRef<AbortController>(new AbortController());
    const containerRef = useRef<HTMLDivElement>(null);

    function escapeListener(evt: KeyboardEvent) {
        if (escapePressed(evt)) onClose();
    }

    function clickListener() {
        const div = containerRef.current;
        if (!div || !hasHover(div)) onClose();
    }

    function renderSelectContainer() {
        return (
            <div className={classNames('product-select-container', open && 'open')} ref={containerRef}>
                <BolmateInput
                    onChange={onChangeSearch}
                    value={search}
                    label={t('reviewAnalytics.feedProductSelect.chooseProduct', 'Kies een product')}
                    placeholder={t('reviewAnalytics.feedProductSelect.nameOrEan', 'Naam of EAN')}
                    onEnter={onSearch}
                    className='search-field'
                    id='search-field'
                />
                <div className='list-container'>
                    <InfiniteList
                        onLoadMore={onLoadMore}
                        renderRow={renderProduct}
                        hasMore={hasMore}
                        isLoadingMore={loadingMore}
                        itemCount={products.length}
                        itemSize={remToPx(3)}
                    />
                </div>
                {!products.length && !loading && (
                    <label className='empty-label'>
                        {t(
                            'reviewAnalytics.feedProductSelect.emptyMessage',
                            'Data aan het verzamelen, kom morgen terug...'
                        )}
                    </label>
                )}
                {(loading || loadingMore) && <DotsLoader dotSize='1.5rem' className='products-loader' noSmile />}
            </div>
        );
    }

    function renderProduct(props: RenderRowProps) {
        const {index, isLoaded, style} = props;
        if (!isLoaded || index < 0 || index > products.length - 1)
            return (
                <div className='list-row loading' style={style}>
                    <div />
                    <div />
                </div>
            );
        const product = products[index];
        return (
            <div className='list-row' onClick={onClickProduct(product)} style={style}>
                <img src={product.image_url} className='product-image' alt='' />
                <div className='title-container'>
                    <label className='product-title'>{product.title}</label>
                    <label className='ean'>{product.ean}</label>
                </div>
            </div>
        );
    }

    useEffect(() => {
        if (open) {
            window.setTimeout(() => {
                document.addEventListener('keydown', escapeListener, {signal: eventController.current.signal});
                document.addEventListener('click', clickListener, {signal: eventController.current.signal});
            });
        } else {
            eventController.current.abort();
            eventController.current = new AbortController();
        }

        return function () {
            eventController.current.abort();
            eventController.current = new AbortController();
        };
    }, [open, onClose]);

    return (
        <div className='feed-product-select'>
            <BolmateButton
                onClick={onOpen}
                label={t('reviewAnalytics.feedProductSelect.analyseWithAI', 'Analyseren met BolmateAI...')}
                className='btn-filter'
                ornaments={{
                    element: (
                        <Image
                            name='logo-no-text'
                            alt=''
                            style={{
                                maxWidth: '1.2rem',
                                maxHeight: '1.2rem',
                                objectFit: 'scale-down'
                            }}
                        />
                    )
                }}
            />
            {renderSelectContainer()}
        </div>
    );
}
